import { ReactNode } from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Link as RouterLink } from 'react-router-dom'
import ContentContainer from '../../areas/layout/content-container'
import HistoryIcon from '@mui/icons-material/History'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import FavoriteIcon from '@mui/icons-material/Favorite'
import StarHalfIcon from '@mui/icons-material/StarHalf'
import ForumIcon from '@mui/icons-material/Forum'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import LiveTvIcon from '@mui/icons-material/LiveTv'
import PublicIcon from '@mui/icons-material/Public'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'
import BedtimeIcon from '@mui/icons-material/Bedtime'
import DownloadIcon from '@mui/icons-material/Download'
import TuneIcon from '@mui/icons-material/Tune'
import InsightsIcon from '@mui/icons-material/Insights'
import TheatersIcon from '@mui/icons-material/Theaters'
import MoodIcon from '@mui/icons-material/Mood'
import CasinoRoundedIcon from '@mui/icons-material/CasinoRounded'
import RestoreIcon from '@mui/icons-material/Restore'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'

const PageWrapper = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(8),
}))

const HeroSection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(8),
}))

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 42,
  fontWeight: 700,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    fontSize: 32,
  },
}))

const Tagline = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  color: theme.palette.text.secondary,
  maxWidth: 600,
  margin: '0 auto',
  lineHeight: 1.6,
  [theme.breakpoints.down('md')]: {
    fontSize: 16,
  },
}))

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(6),
}))

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: 24,
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(3),
  textAlign: 'center',
  [theme.breakpoints.down('md')]: {
    fontSize: 20,
  },
}))

const FeaturesGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: theme.spacing(3),
}))

const FeatureCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}))

const FeatureIcon = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: 12,
  backgroundColor: theme.palette.brand.main + '20',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  '& svg': {
    fontSize: 24,
    color: theme.palette.brand.main,
  },
}))

const FeatureTitle = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1),
}))

const FeatureDescription = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
}))

const FooterNote = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.text.secondary,
  textAlign: 'center',
  '& a': {
    color: theme.palette.brand.main,
  },
}))

type GuideItem = { icon: ReactNode; title: string; description: string }
type GuideSection = { title: string; items: GuideItem[] }

const sections: GuideSection[] = [
  {
    title: 'Track what you watch',
    items: [
      {
        icon: <HistoryIcon />,
        title: 'Watch history',
        description:
          'Mark movies as watched and log shows episode by episode. Progress bars show exactly where you are in every season.',
      },
      {
        icon: <PlaylistAddCheckIcon />,
        title: 'Watchlist',
        description:
          'Save anything you want to watch later. We even remind you when a watchlisted movie hits theaters.',
      },
      {
        icon: <FavoriteIcon />,
        title: 'Favorites',
        description: 'Keep a collection of the titles you love so they are always one click away.',
      },
      {
        icon: <InsightsIcon />,
        title: 'Stats & recaps',
        description:
          'Your profile shows totals, streaks, and top genres - and monthly recap plus year-in-review emails wrap it all up for you.',
      },
      {
        icon: <EmojiEventsIcon />,
        title: 'Badges',
        description:
          'Earn milestone badges as you watch. Your profile shows every badge - earned ones lit up, locked ones with a progress bar and how far you have left.',
      },
    ],
  },
  {
    title: 'Rate & discuss',
    items: [
      {
        icon: <StarHalfIcon />,
        title: 'Personal ratings',
        description:
          'Rate movies, shows, and individual episodes on a 1-10 scale with half-star steps. Ratings unlock once you have watched the title.',
      },
      {
        icon: <ForumIcon />,
        title: 'Comments & replies',
        description:
          'Comment on any movie, show, or episode, reply to other members, and read TMDB reviews in the same feed - or hide them in Settings.',
      },
      {
        icon: <VisibilityOffIcon />,
        title: 'Spoiler protection',
        description:
          'Turn on Hide Spoilers and unwatched episode stills and overviews stay blurred until you mark them watched.',
      },
    ],
  },
  {
    title: 'Discover',
    items: [
      {
        icon: <TrendingUpIcon />,
        title: 'Browse & filter',
        description:
          'Trending, popular, top rated, now playing, upcoming - plus provider filters for HBO Max, Apple TV+, Prime Video, Hulu, and Disney+.',
      },
      {
        icon: <MoodIcon />,
        title: 'What are you in the mood for?',
        description:
          'Pick a mood chip on the home page - Brain off, Scare me, Make me cry, and more - and get an instant rail of matches you have not watched yet.',
      },
      {
        icon: <CasinoRoundedIcon />,
        title: 'Randomizer',
        description:
          'Build a pool from your watchlist, favorites, search, or a mood, then spin to pick tonight\'s watch. One veto allowed - the second spin is final.',
      },
      {
        icon: <RestoreIcon />,
        title: 'Recently viewed',
        description:
          'Jump back to titles you opened: focus the empty search box, open the Recently viewed tab on your profile, or use the Recent drawer while browsing on desktop.',
      },
      {
        icon: <LiveTvIcon />,
        title: 'Where to watch',
        description: 'Every detail page shows which streaming services carry the title in your region.',
      },
      {
        icon: <TheatersIcon />,
        title: 'Details that matter',
        description:
          'Cast, crew, trailers, collections, recommendations, and season-by-season breakdowns for every show.',
      },
      {
        icon: <PublicIcon />,
        title: 'Your region',
        description:
          'Pick your country in Settings and release dates and theater listings match where you live.',
      },
    ],
  },
  {
    title: 'Stay in the loop',
    items: [
      {
        icon: <NotificationsActiveIcon />,
        title: 'Email alerts & digests',
        description:
          'New episodes, new seasons, finales, weekly new releases, trending picks - each type has its own toggle in Settings.',
      },
      {
        icon: <PhoneIphoneIcon />,
        title: 'Push notifications',
        description:
          'The iOS app pings you the day a tracked show gets a new episode, season, or finale. Tap it to jump straight to the show.',
      },
      {
        icon: <BedtimeIcon />,
        title: 'Quiet hours & frequency',
        description:
          'Set a do-not-disturb window and a frequency cap, and notifications politely wait their turn.',
      },
    ],
  },
  {
    title: 'Your account, your data',
    items: [
      {
        icon: <TuneIcon />,
        title: 'Make it yours',
        description:
          'Dark or light theme, default landing page, display name, privacy toggle - all synced between the website and the iOS app.',
      },
      {
        icon: <DownloadIcon />,
        title: 'Export everything',
        description:
          'Download your full history, watchlist, ratings, comments, and browse history as JSON any time from Settings.',
      },
      {
        icon: <VisibilityOffIcon />,
        title: 'Browse history, your call',
        description:
          'Recently viewed only works if you want it to: switch it off or wipe it completely in Settings > Privacy. Only you can ever see it.',
      },
    ],
  },
]

const GuidePage = () => {
  return (
    <ContentContainer>
      <PageWrapper>
        <HeroSection>
          <Title>What you can do on Movieception</Title>
          <Tagline>
            A quick tour of everything the site and the iOS app can do for you. All of it is free -
            just sign in and start tracking.
          </Tagline>
        </HeroSection>

        {sections.map((section) => (
          <Section key={section.title}>
            <SectionTitle>{section.title}</SectionTitle>
            <FeaturesGrid>
              {section.items.map((item) => (
                <FeatureCard key={item.title}>
                  <FeatureIcon>{item.icon}</FeatureIcon>
                  <FeatureTitle>{item.title}</FeatureTitle>
                  <FeatureDescription>{item.description}</FeatureDescription>
                </FeatureCard>
              ))}
            </FeaturesGrid>
          </Section>
        ))}

        <FooterNote>
          Ready to dive in? Head to the <RouterLink to='/discover'>Discover page</RouterLink> or open{' '}
          <RouterLink to='/profile/settings'>Settings</RouterLink> to tune everything to your taste.
        </FooterNote>
      </PageWrapper>
    </ContentContainer>
  )
}

export default GuidePage
